import React, { Fragment, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { EVENTS } from '../../analytics/events'
import { useDispatch, useSelector } from 'react-redux'
import { selectEventsData, setEventDataList } from '../events/eventsSlice'
import { selectPlan, selectShowCheckout, setDefaultPlanId, setPlan, setShowCheckout } from './plansSlice'
import { fontSize, wh } from '../../mixins'
import UIButton from '../../UI/UIButton'
import { sendAnalyticsEvents } from '../../analytics/common'
import { PLANS } from '../../analytics/eCommerce'
import MainContainer from '../../UI/MainContainer'
import { selectStripeAccountName } from '../signup/signupSlice'

const isArabic = navigator.language.startsWith('ar')
const textAlign = isArabic ? 'right' : 'left'

const StyledPlanList = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  padding: 0;
  position: relative;
  width: 100%;
  align-items: center;
  > div {
    text-align: left;
    width: 100%;
    padding-left: 10px;
  }
`

const StyledLi = styled.li`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-direction: column;
  align-items: center;
  background: #fff;
  opacity: ${({ active }) => (active ? 1 : 0.6)};
  border-radius: 16px;
  ${wh('100%', '90px')};
  padding: 18px 16px;
  max-width: 320px;
  cursor: pointer;
  &:first-child {
    height: 52px;
    margin-bottom: 6px;
    padding: 15px;
  }
  @media screen and (max-width: 320px) {
    padding: 5px;
    height: auto;
    &:first-child {
      height: auto;
      padding: 5px;
    }
  }
`
const StyledPriceWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const StyledMonthlyPrice = styled.span`
  font-weight: 700;
  ${fontSize(16)};
  line-height: 24px;
  color: #110021;
  opacity: ${({ active }) => (active ? 1 : 0.6)};
`

const StyledWeeklyPrice = styled.span`
  color: ${({ active }) => (active ? 'rgba(17, 0, 33, 0.85)' : '#151515')};
  opacity: ${({ active }) => (active ? 1 : 0.64)};
  font-weight: 700;
  ${fontSize(14)};
  line-height: 16px;
`
const StyledAnnualWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 4px;
`
const StyledAnnualText = styled.span`
  font-weight: 400;
  ${fontSize(12)};
  line-height: 20px;
  color: #110021;
  opacity: ${({ active }) => (active ? 1 : 0.6)};
`

const StyledAnnualSave = styled.span`
  background: #3393ec;
  padding: 4px 8px;
  font-weight: 700;
  ${fontSize(10)};
  line-height: 12px;
  text-transform: uppercase;
  color: #ffffff;
  border-radius: 6px;
`

const Title = styled.h1`
  ${fontSize(30)};
  font-weight: 400;
  line-height: 38px;
  color: #323232;
  text-align: center;
  > span {
    color: #3393ec;
  }
`

const IntroWrapper = styled.div`
  width: 100%;
  position: relative;
`

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 auto 20px;
  max-width: 300px;
  @media screen and (max-width: 320px) {
    padding: 20px;
  }
`

const StyledTrialText = styled.div`
  opacity: ${({ active }) => (active ? '1' : '0')};
  transition: opacity 0.5s;
  margin-top: 12px;
  margin-bottom: 8px;
  font-weight: 400;
  ${fontSize(14)};
  line-height: 20px;
  color: #ffffff;
`

const StyledItem = styled.li`
    position: relative;
    padding-${textAlign}: 20px;
    font-weight: 400;
    ${fontSize(16)};
    line-height: 19px;
    color: #323232;
    text-align: ${textAlign};
  
    svg {
        ${wh('20px')};
        position: absolute;
        top: 0px;
        ${textAlign}: 0;
    }
    div {
        padding-left: 14px;
    }
  
    &:not(:last-child) {
        margin-bottom: 16px;
    }
`

const PlanWrapper = styled.div`
  background-color: #3393ec;
  border-radius: 47px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  max-width: 375px;
  margin: 0 auto;
`

const IntroPlan = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const plan = useSelector(selectPlan)
  const eventsData = useSelector(selectEventsData)
  const stripeAccountName = useSelector(selectStripeAccountName)
  const showCheckout = useSelector(selectShowCheckout)
  const INTRO_PLANS = useMemo(() => {
    return [
      {
        id: 'fl-monthly-999',
        period: t('monthly'),
        price: '$2.49',
        week: t('week'),
        bestValue: true,
        fullPrice: '$9.99',
      },
      {
        id: 'fl-annually-free7d-4999',
        title: '12',
        period: t('month'),
        price: '$2.08',
        week: t('week'),
        fullPrice: '$49.99',
        save: '$58',
      },
    ]
  }, [t])
  useEffect(() => {
    if (!showCheckout) {
      dispatch(setPlan(INTRO_PLANS[1].id))
      dispatch(setDefaultPlanId(INTRO_PLANS[1].id))
    }
  }, [dispatch, INTRO_PLANS, showCheckout])

  const onPlanClick = (id) => dispatch(setPlan(id))

  const onContinueClick = () => {
    const { productId, duration, description, periodPrice, price } = PLANS[plan]
    const planData = {
      Value: price,
      value: Math.round(periodPrice * 100),
      currency: 'USD',
      price: periodPrice,
      Duration: duration,
      duration,
      description,
      periodPrice,
      product_id: productId,
      stripe_account_name: stripeAccountName,
    }
    dispatch(setEventDataList(planData))
    sendAnalyticsEvents(EVENTS.onboardingPaywallChosen, {
      ...eventsData,
      ...planData,
    })
    dispatch(setShowCheckout(true))
  }

  const FEATURES = [
    t('know_when_family_come_or_go'),
    t('realtime_location_sharing'),
    t('find_loved_ones'),
    t('be_notified_in_case'),
    t('get_notified_when_the_land'),
  ]
  const renderFeaturesItem = (i, index) => {
    return (
      <StyledItem key={index}>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.295 9.60598L11.727 15.606C11.539 15.853 11.248 15.999 10.938 16.001H10.931C10.624 16.001 10.334 15.859 10.144 15.617L7.712 12.51C7.372 12.076 7.448 11.447 7.883 11.107C8.317 10.766 8.947 10.842 9.287 11.278L10.92 13.364L14.704 8.39498C15.038 7.95598 15.665 7.86998 16.106 8.20498C16.545 8.53998 16.63 9.16698 16.295 9.60598ZM12 2.00098C6.477 2.00098 2 6.47798 2 12.001C2 17.523 6.477 22.001 12 22.001C17.523 22.001 22 17.523 22 12.001C22 6.47798 17.523 2.00098 12 2.00098Z"
            fill="#007AFF"
          />
        </svg>
        <div>{i}</div>
      </StyledItem>
    )
  }
  const renderOffer = (i, index) => {
    let isActive = plan === i.id
    return (
      <Fragment key={index}>
        <StyledLi active={isActive} onClick={() => onPlanClick(i.id)}>
          {i.save && (
            <StyledAnnualWrapper>
              <StyledAnnualText active={isActive}>
                {t('annual')}
              </StyledAnnualText>
              <StyledAnnualSave>
                {t('save')} {i.save} / {t('year')}
              </StyledAnnualSave>
            </StyledAnnualWrapper>
          )}
          <StyledPriceWrapper>
            <StyledMonthlyPrice active={isActive}>
              {i.title} {i.period} {i.fullPrice}{' '}
            </StyledMonthlyPrice>
            <StyledWeeklyPrice active={isActive}>
              {i.price} / {i.week}
            </StyledWeeklyPrice>
          </StyledPriceWrapper>
        </StyledLi>
        {i.save && (
          <StyledTrialText active={plan === INTRO_PLANS[1].id}>
            {t('try_trial')} {`${i.fullPrice}/${t('year')}.`}
            <br />
            {t('cancel')}
          </StyledTrialText>
        )}
      </Fragment>
    )
  }
  return (
    <MainContainer
      adaptiveHeight={800}
      customPadding={'25px'}
      desktopPadding={'0'}
      customTabletJustify={'center'}
    >
      <IntroWrapper>
        <Title>
          {t('unlock')} <span>{t('family_locator')}</span>
        </Title>
        <StyledList>{FEATURES.map(renderFeaturesItem)}</StyledList>
        <PlanWrapper>
          <StyledPlanList>{INTRO_PLANS.map(renderOffer)}</StyledPlanList>
          <UIButton
            onClick={onContinueClick}
            customColor={'#FFFFFF'}
            customFontColor={'#3393EC'}
            customHover={'#E0DEDE'}
            customActive={'#FFFFFF'}
            customBeforeBg={'none'}
            position={'sticky'}
            bottomPosition={'24px'}
          >
            {t('continue')}
          </UIButton>
        </PlanWrapper>
      </IntroWrapper>
    </MainContainer>
  )
}

export default IntroPlan
